import { GetActiveCardFromCustomerByUserIdQuery } from '~/api';

export type StampCardData = GetActiveCardFromCustomerByUserIdQuery['getActiveCardFromCustomerByUserId'];

/**
 * Validates if stamp card data is complete and ready for display
 * @param stampCardData - The stamp card data from GraphQL query
 * @returns boolean indicating if the stamp card data is valid
 */
export const isValidStampCard = (stampCardData: StampCardData | null | undefined): stampCardData is NonNullable<StampCardData> => {
  try {
    if (!stampCardData) {
      return false;
    }

    // Defensive check for object structure
    if (typeof stampCardData !== 'object') {
      return false;
    }

    // Check for required fields that indicate a valid stamp card
    const hasRequiredFields = 
      typeof stampCardData.totalStampsRequired === 'number' &&
      stampCardData.totalStampsRequired >= 0 &&
      typeof stampCardData.totalStampsEarned === 'number' &&
      stampCardData.totalStampsEarned >= 0;

    return hasRequiredFields;
  } catch (error) {
    // Silently handle validation errors in production
    return false;
  }
};

/**
 * Safely extracts stamp card props for the StampCard component
 * @param stampCardData - The stamp card data from GraphQL query
 * @returns Processed stamp card data or undefined if invalid
 */
export const getStampCardProps = (stampCardData: StampCardData | null | undefined) => {
  try {
    if (!isValidStampCard(stampCardData)) {
      return undefined;
    }

    // Additional safety checks for extracted values
    const total = Math.max(0, stampCardData.totalStampsRequired || 0);
    const earned = Math.max(0, Math.min(total, stampCardData.totalStampsEarned || 0));

    return {
      data: stampCardData,
      total,
      earned,
    };
  } catch (error) {
    // Silently handle processing errors in production
    return undefined;
  }
};

/**
 * Helper to determine if add stamps modal should be enabled
 * @param stampCardData - The stamp card data from GraphQL query
 * @returns boolean indicating if add stamps functionality is available
 */
export const canAddStamps = (stampCardData: StampCardData | null | undefined): boolean => {
  try {
    if (!isValidStampCard(stampCardData)) {
      return false;
    }

    // Defensive numeric validation
    const earned = typeof stampCardData.totalStampsEarned === 'number' ? stampCardData.totalStampsEarned : 0;
    const required = typeof stampCardData.totalStampsRequired === 'number' ? stampCardData.totalStampsRequired : 0;

    // Can add stamps if the card is not already complete and required stamps is positive
    return required > 0 && earned < required;
  } catch (error) {
    // Silently handle validation errors in production
    return false;
  }
};

/**
 * Gets the stamp card progress information
 * @param stampCardData - The stamp card data from GraphQL query
 * @returns Object with progress details or null if invalid
 */
export const getStampCardProgress = (stampCardData: StampCardData | null | undefined) => {
  try {
    if (!isValidStampCard(stampCardData)) {
      return null;
    }

    // Defensive numeric validation and bounds checking
    const earned = Math.max(0, typeof stampCardData.totalStampsEarned === 'number' ? stampCardData.totalStampsEarned : 0);
    const required = Math.max(0, typeof stampCardData.totalStampsRequired === 'number' ? stampCardData.totalStampsRequired : 0);
    
    // Cap earned stamps to not exceed required stamps for calculation consistency
    const cappedEarned = Math.min(earned, required);
    
    const percentage = required > 0 ? Math.round((cappedEarned / required) * 100) : 0;
    const isComplete = earned >= required && required > 0;

    return {
      earned,
      required,
      percentage: Math.max(0, Math.min(100, percentage)), // Ensure percentage is between 0-100
      isComplete,
      remaining: Math.max(0, required - earned),
    };
  } catch (error) {
    // Silently handle calculation errors in production
    return null;
  }
};